/**
 * Create Or Edit Shape Tool Schema
 * 
 * Create a new shape or edit an existing shape on a slide.
 * Uses TEXT_CONTENT_SCHEMA_WITH_OPERATIONS for text editing.
 */

import { z } from "zod";

// Import shared schemas from liveBuildArea
import {
    PositionSchema,
    FillSchema,
    OutlineSchema,
    ShadowSchema,
    TextFrameSchema,
    BulletSchema,
    TextFormatSchema,
    ParagraphPayloadSchema,
    MetadataEntrySchema,
} from "../../../tools/liveBuildArea/liveBuildArea.schema";

// ═══════════════════════════════════════════════════════════════════════════
// PARAGRAPH OPERATIONS SCHEMAS (for edit mode)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Paragraph data for insert/edit operations
 */
export const ParagraphDataSchema = ParagraphPayloadSchema;

/**
 * Paragraph operation schema (for edit mode)
 */
export const ParagraphOperationSchema = z.object({
    operation: z.enum(["insert", "remove", "edit"])
        .describe("Operation type: insert (add new), remove (delete), edit (modify)"),
    
    index: z.number().int().min(0)
        .describe("0-based paragraph index (remove=original, insert=post-removal, edit=post-insert)"),
    
    inheritFormattingFrom: z.number().int().min(0).optional()
        .describe("For INSERT: copy formatting from this paragraph index"),
    
    spacingConsequences: z.string().optional()
        .describe("Impact description: 'Adding long paragraph, text box may grow ~25-30pt vertically'"),
    
    shapesAffectedBySpacingConsequences: z.array(z.number().int()).optional()
        .describe("Shape IDs affected by text box size changes"),
    
    paragraph: ParagraphDataSchema.optional()
        .describe("Paragraph data (required for INSERT and EDIT)"),
});

/**
 * Text content schema with operations (for createOrEdit tools)
 */
export const TextContentWithOperationsSchema = z.object({
    paragraphOperations: z.array(ParagraphOperationSchema)
        .describe("Array of paragraph operations (insert/remove/edit)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// METADATA SCHEMA (edit mode - with remove/add)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Metadata operations for edit mode
 */
export const MetadataOperationsSchema = z.object({
    removeEntryIds: z.array(z.string()).optional()
        .describe("Source context IDs to remove (processed FIRST)"),
    
    addEntries: z.array(MetadataEntrySchema).optional()
        .describe("Metadata entries to add (AFTER removals)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// INHERIT SHAPE PROPERTIES SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Inherit shape properties schema
 */
export const InheritShapePropertiesSchema = z.object({
    source: z.object({
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        shapeId: z.number().int(),
    }),
    properties: z.array(z.enum(["fill", "outline", "shadow", "margins"])),
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN CREATE OR EDIT SHAPE PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create Or Edit Shape parameters schema
 */
export const CreateOrEditShapeParamsSchema = z.object({
    slideId: z.number().int().min(1)
        .describe("1-based slide index"),
    
    operationType: z.enum(["create", "edit"])
        .describe("'create' for new shape, 'edit' for existing shape"),
    
    shapeId: z.number().int().optional()
        .describe("Shape ID (REQUIRED when operationType='edit')"),
    
    purpose: z.string().optional()
        .describe("Documentation: what this shape is for"),
    
    notes: z.string().optional()
        .describe("Additional notes for documentation"),
    
    // Position & Transform
    position: PositionSchema.optional()
        .describe("Shape position and dimensions"),
    
    rotation: z.number().optional()
        .describe("Rotation in degrees clockwise"),
    
    zOrder: z.number().int().optional()
        .describe("Stacking order (higher = on top)"),
    
    // Styling
    fill: FillSchema.optional()
        .describe("Fill styling (solid or image)"),
    
    outline: OutlineSchema.optional()
        .describe("Outline/border styling"),
    
    shadow: ShadowSchema.optional()
        .describe("Shadow styling"),
    
    // Shape type (for create)
    shapeType: z.enum(["textbox", "rectangle", "ellipse", "line", "triangle", "arrow"]).optional()
        .describe("Shape type (REQUIRED when operationType='create')"),
    // TODO: Consider adding callout shape types (commonly used)
    
    // Inherit properties
    inheritShapeProperties: InheritShapePropertiesSchema.optional()
        .describe("Copy specific visual properties from another shape (e.g., fill/outline/shadow/margins) to keep styling consistent."),
    
    // Text
    textFrame: TextFrameSchema.optional()
        .describe("Text frame properties"),
    
    textContent: TextContentWithOperationsSchema.optional()
        .describe("Text content with paragraph operations"),
    
    // Metadata (edit mode)
    metadata: MetadataOperationsSchema.optional()
        .describe("Unified metadata (edit mode: removeEntryIds + addEntries)"),
}).refine(
    (data) => {
        // shapeType required for create
        if (data.operationType === "create" && !data.shapeType) {
            return false;
        }
        // shapeId required for edit
        if (data.operationType === "edit" && !data.shapeId) {
            return false;
        }
        return true;
    },
    {
        message: "shapeType required for create mode, shapeId required for edit mode",
    }
);

export type CreateOrEditShapeParams = z.infer<typeof CreateOrEditShapeParamsSchema>;
export type ParagraphOperation = z.infer<typeof ParagraphOperationSchema>;
export type ParagraphData = z.infer<typeof ParagraphDataSchema>;
export type TextContentWithOperations = z.infer<typeof TextContentWithOperationsSchema>;
export type MetadataOperations = z.infer<typeof MetadataOperationsSchema>;

